import { z } from 'zod'

/**
 * Nearby destination index (#861, toolkit: Searchbar Pro `enable_nearby`).
 *
 * The toolkit feature is a DESTINATION GUESSER, not a radius property search:
 * it turns the visitor's coordinates into the ONE closest destination term and
 * then runs the site's ordinary term search (`selectItem(items[0])` in
 * searchbar-pro.js). This module is the platform half of that — the coordinate
 * index it resolves against, plus the resolver itself.
 *
 * Coordinates come from the SAME place the toolkit's do: no geocoding service
 * is involved anywhere. `DestinationIndex\Indexer::compute_geo()` averages the
 * coordinates of the properties carrying a term; this derives the same centroid
 * from the feed's own `location.lat/lng`, which the public properties endpoint
 * already returns. The index is built by the dashboard (an operator action,
 * the analogue of the toolkit's daily WP-Cron reindex) and stored in widget
 * settings, so the visitor's position never leaves the browser — the resolver
 * below runs client-side against stored rows.
 */

/** Which visitor-filter dimension a centroid resolves to. The Where field can
 *  own either, per `whereFieldSource` (city | location | zone). */
export const nearbyFilterKeys = ['cities', 'locations'] as const
export type NearbyFilterKey = (typeof nearbyFilterKeys)[number]

/** Settings ship to every visitor and outlive the writer, so the stored index
 *  is bounded like the destination tree (#1072): a damaged or hostile blob must
 *  cost a bounded parse, and rows that fail drop rather than throw the render. */
export const MAX_NEARBY_CENTROID_ROWS = 2000

/** Per-dimension ceiling. The total cap alone is not enough: rows sort by
 *  dimension, so slicing the total would drop ALL `locations` from a feed with
 *  more than the cap in `cities` and leave a location-source Where field
 *  permanently answering "nothing near you". Halving the budget bounds the blob
 *  the same way while guaranteeing neither dimension can be erased. */
export const MAX_NEARBY_ROWS_PER_DIMENSION = MAX_NEARBY_CENTROID_ROWS / 2

export const nearbyCentroidSchema = z.object({
  filterKey: z.enum(nearbyFilterKeys).describe('Visitor-filter dimension this centroid applies to'),
  // positive(): term ids start at 1, and coerce would turn a null/'' id into 0
  // and keep a row the per-row safeParse should drop.
  id: z.coerce.number().int().positive().describe('Central term id the Where field writes'),
  name: z.string().min(1).max(200).describe('Term name, shown in the "searching near X" feedback'),
  // finite(): NaN/Infinity survive z.number() and would poison every distance
  // comparison (NaN <= max is false, so a poisoned row silently never wins).
  lat: z.coerce.number().finite().min(-90).max(90),
  lng: z.coerce.number().finite().min(-180).max(180),
  count: z.coerce.number().int().nonnegative().default(0).describe('Properties that fed this centroid at build time'),
})

export type NearbyCentroid = z.infer<typeof nearbyCentroidSchema>

/** The stored index. Lenient like the destination tree: parse runs at RENDER
 *  time, so a damaged blob must degrade to "no index" (the row hides), never
 *  throw (#858, #1168). */
export const nearbyCentroidsField = () =>
  z.preprocess(
    // Slice BEFORE the per-row parse so the cap bounds the WORK a hostile blob
    // can cause, not just the stored length.
    (v) => (Array.isArray(v)
      // eslint-disable-next-line no-restricted-syntax -- per-row leniency inside a preprocess wrapper, the sanctioned safeParse use (#1168)
      ? v.slice(0, MAX_NEARBY_CENTROID_ROWS).filter((row) => nearbyCentroidSchema.safeParse(row).success)
      : undefined),
    z.array(nearbyCentroidSchema).default([]),
  ).describe('Derived destination coordinates for the Nearby row, written by the dashboard\'s "Build nearby index" action, not hand-edited. Empty = no index built, and the Nearby row stays hidden.')

/** A property as the public feed endpoint returns it, narrowed to what a
 *  centroid needs. `city` is a single term; `locations` is a list. */
export type NearbyDerivationProperty = {
  location?: { lat?: number | null; lng?: number | null } | null
  city?: { id?: number | string | null; name?: string | null } | null
  locations?: Array<{ id?: number | string | null; name?: string | null }> | null
}

/** The (0,0) sentinel. The toolkit rejects it on both sides (its indexer skipped
 *  it, and `handle_nearby` 400s on it) because a property whose coordinates
 *  never got filled in reads as the Gulf of Guinea and would drag any centroid
 *  it joined halfway across the Atlantic. */
const isUsableCoord = (lat: unknown, lng: unknown): lat is number =>
  typeof lat === 'number' && typeof lng === 'number'
  && Number.isFinite(lat) && Number.isFinite(lng)
  && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
  && !(lat === 0 && lng === 0)

/**
 * Average each term's property coordinates into one centroid, the toolkit's
 * `compute_geo()` rule. Terms whose properties carry no usable coordinate
 * produce NO row at all rather than a (0,0) row — toolkit#264 shipped exactly
 * that bug (every centroid stored at 0,0) and every site had to reindex.
 *
 * A plain arithmetic mean is deliberate parity, not an oversight: it is wrong
 * near the antimeridian (a term spanning ±180° averages to the middle of the
 * Pacific), but destinations that straddle it do not exist in practice on this
 * fleet, and diverging from the toolkit here would make the two products
 * disagree about which destination is closest.
 */
export function deriveNearbyCentroids(properties: NearbyDerivationProperty[]): NearbyCentroid[] {
  const acc = new Map<string, { filterKey: NearbyFilterKey; id: number; name: string; lat: number; lng: number; count: number }>()

  const add = (filterKey: NearbyFilterKey, rawId: unknown, rawName: unknown, lat: number, lng: number) => {
    const id = typeof rawId === 'string' ? Number(rawId) : rawId
    // isSafeInteger, not isInteger: '9007199254740993' parses to the SAME
    // float as '9007199254740992', which would merge two distinct terms
    // under one key and emit a filter id that identifies neither.
    if (typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0) return
    const name = typeof rawName === 'string' ? rawName.trim() : ''
    if (!name) return
    const key = `${filterKey}:${id}`
    const row = acc.get(key)
    if (row) {
      row.lat += lat
      row.lng += lng
      row.count += 1
    } else {
      acc.set(key, { filterKey, id, name: name.slice(0, 200), lat, lng, count: 1 })
    }
  }

  for (const p of properties) {
    const lat = p.location?.lat
    const lng = p.location?.lng
    if (!isUsableCoord(lat, lng)) continue
    if (p.city) add('cities', p.city.id, p.city.name, lat, lng as number)
    // Deduplicate within the property: a feed that lists the same region twice
    // would otherwise weight that property twice in its centroid and pull it
    // toward the duplicated listing.
    const seenLocations = new Set<unknown>()
    for (const loc of p.locations ?? []) {
      if (loc?.id === undefined || loc?.id === null || seenLocations.has(String(loc.id))) continue
      seenLocations.add(String(loc.id))
      add('locations', loc.id, loc?.name, lat, lng as number)
    }
  }

  const rows = [...acc.values()]
    .map((r) => ({
      filterKey: r.filterKey,
      id: r.id,
      name: r.name,
      // Round at storage: seven decimals is ~11mm, far past what a centroid of
      // whole properties means, and it keeps the settings blob from carrying
      // 15 digits of float noise per row.
      lat: Math.round((r.lat / r.count) * 1e7) / 1e7,
      lng: Math.round((r.lng / r.count) * 1e7) / 1e7,
      count: r.count,
    }))
    // Stable, human-readable order (dimension, then id) so a rebuild that finds
    // the same data produces a byte-identical blob and does not dirty settings.
    .sort((a, b) => (a.filterKey === b.filterKey ? a.id - b.id : a.filterKey < b.filterKey ? -1 : 1))

  return nearbyFilterKeys.flatMap((key) =>
    rows.filter((r) => r.filterKey === key).slice(0, MAX_NEARBY_ROWS_PER_DIMENSION))
}

/** Which dimensions hit the per-dimension ceiling, so the build panel can say
 *  what was dropped instead of presenting a truncated index as complete. */
export function truncatedNearbyDimensions(rows: NearbyCentroid[]): NearbyFilterKey[] {
  return nearbyFilterKeys.filter(
    (key) => rows.filter((r) => r.filterKey === key).length >= MAX_NEARBY_ROWS_PER_DIMENSION)
}

const EARTH_RADIUS_KM = 6371
const toRad = (deg: number) => (deg * Math.PI) / 180

/** Great-circle distance in km. The toolkit computes the same thing in SQL
 *  (spherical law of cosines, R = 6371, ACOS clamped); haversine is the
 *  numerically stable form of it and agrees to well under a metre at these
 *  distances. */
export function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const dLat = toRad(bLat - aLat)
  const dLng = toRad(bLng - aLng)
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** The toolkit's ceiling, filterable there as `hrtk_sbp_nearby_max_km`. Kept a
 *  constant rather than a setting because it is not an admin field there
 *  either — a visitor 400 km from every destination gets "no nearby
 *  destinations", which is the honest answer. */
export const NEARBY_MAX_KM = 300

export type NearbyMatch = { centroid: NearbyCentroid; distanceKm: number }

/**
 * Nearest centroid within the radius, or null.
 *
 * Restricted to the dimensions the Where field actually owns: resolving to a
 * `locations` term on a city-source field would write a filter the visitor
 * cannot see or clear in that field.
 *
 * Ties break on the LOWER term id. The toolkit's SQL has no tie-break at all
 * (`ORDER BY distance_km ASC`), so equidistant destinations come back in
 * whatever order MySQL felt like; a deterministic rule is what makes this
 * testable and keeps two visitors at the same spot seeing the same answer.
 */
export function nearestCentroid(
  centroids: NearbyCentroid[],
  pos: { lat: number; lng: number },
  ownedKeys: readonly string[],
  maxKm: number = NEARBY_MAX_KM,
): NearbyMatch | null {
  let best: NearbyMatch | null = null
  for (const centroid of centroids) {
    if (!ownedKeys.includes(centroid.filterKey)) continue
    const distanceKm = haversineKm(pos.lat, pos.lng, centroid.lat, centroid.lng)
    if (distanceKm > maxKm) continue
    if (
      best === null
      || distanceKm < best.distanceKm
      || (distanceKm === best.distanceKm && centroid.id < best.centroid.id)
    ) {
      best = { centroid, distanceKm }
    }
  }
  return best
}

/** Coordinate sanity, client-side. The toolkit validates this server-side
 *  (`invalid_coordinates`, plus an explicit (0,0) sentinel rejection); with no
 *  server in this port the checks move here rather than disappear — a device
 *  that reports (0,0) must read as "couldn't get your location", not as a
 *  visitor floating off West Africa. */
export const isUsablePosition = (lat: unknown, lng: unknown): boolean => isUsableCoord(lat, lng)
